import { store, store$ } from '@redux/store';
import Carousel from '@components/carousel/dynamicCarousel';
import LoadingStatus from 'enums/loadingStatus';
import { distinctUntilChanged, filter, map } from 'rxjs';
import tourDateSummary from './tourDateSummary';
import ContactPhone from './contactPhone';
import checkSameLocalDay from '../utils/checkSameLocalDay';
import { setModalStep, setSelectedSlot } from '../redux/slices';
import DateInput from './dateInput';
import SlotCarousel from './slotsCarousel';
import getAvailableTourDates from '../apis/getAvailableTourDates';
import ModalStep from '../enums/modalStep';
import { selectModalStep, selectSelectedSlot } from '../redux/selectors';
import getSpecTourableSiblings from '../apis/getSpecTourableSiblings';

/**
 * Options for the month formatter
 * Using America/Chicago IANA time zone name as default in place of CT time zone abbreviation
 * @type {{timeZone: string, month: string}}
 */
const monthFormatterOptions = { timeZone: 'America/Chicago', month: 'long' };
const zoneFormatterOptions = {
  timeZone: 'America/Chicago',
  timeZoneName: 'long'
};

export default class DateTimeForm {
  // todo: evaluate if we need a default value for the date, representing the selected date
  //  as different components are assuming "Now" as default independently when this value is null
  date = null;

  slots = [];

  selectedSlot = null;

  timezoneSelector = '[data-tour-timezone]';

  slotDateTimeSelector = '[data-selected-slot-summary]';

  constructor(rootComponent) {
    this.dateInitialSlot = null;
    this.rootComponent = rootComponent;
    this.salesNumber = rootComponent.specData.salesNumber;
    this.nextStep = ModalStep.TourConfiguration;
    this.salesPhone = new ContactPhone(rootComponent);
    this.dateInput = new DateInput(
      rootComponent,
      this.date,
      this.handleDateChange.bind(this)
    );

    const carousel = new Carousel();
    this.slotsCarousel = new SlotCarousel(
      rootComponent,
      carousel,
      this.handleSelectSlot.bind(this)
    );
    this.onCloseModal$ = store$.pipe(
      map(selectModalStep),
      distinctUntilChanged(),
      filter(step => !step)
    );
    this.onCloseModal$.subscribe(this.setDefaultValues.bind(this));
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate.bind(this));
  }

  /**
   * Resets the default values for the terms and conditions and sale status.
   */
  setDefaultValues() {
    this.date = this.dateInitialSlot;
    this.dateInput.date = this.dateInitialSlot;
  }

  onDomUpdate(event) {
    if (event.detail && event.detail.step === ModalStep.DateTimeSelection) {
      this.handleFetchSlots(this.date);
      this.rootComponent.setSecondaryButtonDisabled(
        this.rootComponent.unavailableTours
      );
    }
  }

  /**
   * Handle the selection of a slot
   * @param {string} slotDate - The date of the selected slot
   */
  handleSelectSlot(slotDate) {
    const slot = this.slots.find(s => s.Date === slotDate);
    if (!slot) {
      return;
    }
    this.updateSelectedSlotSummary(slot);
    store.dispatch(setSelectedSlot({ selectedSlot: slot }));
  }

  /**
   * Handle the change of date
   * @param {Date} newDate - The new date selected
   */
  handleDateChange(newDate) {
    this.handleFetchSlots(newDate);
  }

  /**
   * Fetch available slots for the selected date
   * @param {Date} newDate - The new date selected
   */
  handleFetchSlots(newDate) {
    this.slotsCarousel.update(LoadingStatus.Loading);
    this.rootComponent.setPrimaryButtonDisabled(true);
    let startDate = null;
    let endDate = null;
    if (newDate) {
      const utcOffset = newDate.getTimezoneOffset();
      startDate = new Date(newDate.getTime() + utcOffset * 60000);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 1);
    }
    // we remove the notification if it was shown
    this.rootComponent.removeNotification();
    const { specId } = this.rootComponent.getSpecDetails();

    getAvailableTourDates({
      specId,
      startDate,
      endDate
    })
      .then(responseSlots => {
        if (responseSlots.length === 0) {
          throw new Error('No slots found');
        }

        const firstSlotDate = new Date(responseSlots[0].UtcDate);
        this.slots = responseSlots.filter(slot =>
          checkSameLocalDay(firstSlotDate, new Date(slot.UtcDate))
        );

        this.slotsCarousel.update(LoadingStatus.Succeeded, this.slots);

        const anchorSlot = this.slots[0];
        const utcDate = new Date(anchorSlot.UtcDate);
        this.date = new Date(
          utcDate.getFullYear(),
          utcDate.getMonth(),
          utcDate.getDate()
        );
        if (!this.dateInitialSlot) {
          this.dateInitialSlot = this.date;
        }

        this.updateMonth(anchorSlot);
        this.updateTourTimezone(anchorSlot);
        this.updateSelectedSlotSummary(anchorSlot);
        // we automatically select the first slot
        store.dispatch(setSelectedSlot({ selectedSlot: anchorSlot }));
        this.rootComponent.setPrimaryButtonDisabled(false);

        this.dateInput.update(this.date);
      })
      .catch(() => {
        if (selectModalStep(store.getState()) === ModalStep.DateTimeSelection) {
          this.rootComponent.showNotification(
            'Tour not available at this time. Please select another date.'
          );
          this.updateSelectedSlotSummary(null);
          this.rootComponent.setPrimaryButtonDisabled(true);
          this.slotsCarousel.update(LoadingStatus.Failed);
        }
      });
  }

  /**
   * Advances the modal to the next step based on the number of tourable homes.
   *
   * @param {number} numberOfTourableHomes - The number of homes available for touring.
   * If this value is truthy, the modal progresses to the next predefined step.
   * Otherwise, it moves to the "Terms and Conditions" step.
   */
  static showNextPanel(numberOfTourableHomes) {
    if (numberOfTourableHomes) {
      store.dispatch(setModalStep({ modalStep: ModalStep.TourConfiguration }));
      return;
    }

    store.dispatch(setModalStep({ modalStep: ModalStep.TermsAndConditions }));
  }

  /**
   * Handles the primary action for the component.
   * Dispatches an action to update the modal step in the store with the next step.
   *
   */
  handlePrimaryAction() {
    this.rootComponent.setPrimaryButtonDisabled(true);
    this.fetchTourableSiblings$().subscribe({
      next: tourableHomes => {
        const tourableHomesCollection = {};
        tourableHomes.forEach(item => {
          tourableHomesCollection[item.Id] = item;
        });
        this.rootComponent.otherHomes = tourableHomesCollection;

        DateTimeForm.showNextPanel(tourableHomes.length);
      }
    });
  }

  /**
   * Fetches the list of tourable sibling homes for the current spec.
   */
  fetchTourableSiblings$() {
    const state = store.getState();
    const slot = selectSelectedSlot(state);
    const date = new Date(slot.UtcDate);

    const { specId } = this.rootComponent.getSpecDetails();
    return getSpecTourableSiblings(specId, date);
  }

  /**
   * Handles the secondary action for the component.
   * If a sales number is available in the root component, it redirects the user
   * to initiate a phone call to the sales number.
   */
  handleSecondaryAction() {
    this.rootComponent.callSales();
  }

  /**
   * Update the displayed month based on the selected slot
   * @param {Object} [anchorSlot] - The selected slot
   */
  renderMonth(anchorSlot) {
    const slotsMonthFormat = new Intl.DateTimeFormat('en-US', {
      ...monthFormatterOptions,
      timeZone: anchorSlot ? anchorSlot.SpecTimezoneIANAId : undefined
    });
    //
    return slotsMonthFormat.format(this.date || new Date());
  }

  updateMonth(anchorSlot) {
    document.querySelector('[data-month-selected]').innerHTML =
      this.renderMonth(anchorSlot);
  }

  /**
   * Update the displayed tour timezone based on the selected slot
   * @param {Object} anchorSlot - The selected slot
   */
  updateTourTimezone(anchorSlot) {
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      ...zoneFormatterOptions,
      timeZone: anchorSlot.SpecTimezoneIANAId
    });
    // we only want the timezone name part of the full date string
    const formattedDate = dateFormatter.format(new Date(anchorSlot.UtcDate));
    this.getTimezoneContainer().innerHTML = formattedDate.split(', ')[1];
  }

  getTimezoneContainer() {
    return document.querySelector(this.timezoneSelector);
  }

  /**
   * Update the summary of the selected slot
   * @param {Object} slot - The selected slot
   */
  updateSelectedSlotSummary(slot) {
    const element = this.getSlotDateTimeContainer();
    if (element) {
      element.innerHTML = tourDateSummary(slot);
    }
  }

  getSlotDateTimeContainer() {
    return document.querySelector(this.slotDateTimeSelector);
  }

  /**
   * Render the DateTimeForm component
   * @returns {string} - The HTML string of the component
   */
  render() {
    const { brandName, communityName, planName } =
      this.rootComponent.getSpecDetails();

    return `
      <div class="nhs-c-wrapper__select-date" data-select-date>
            <div class="nhs-c-self-guided-left-panel">
              <div>
                <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large nhs-mq-display--none-large">Select a date &amp; time</h1>

                <div class="nhs-c-notification__placeholder" data-self-guided-tour-notifications></div>

                <h2 class="nhs-c-self-guided-select-date__intro">
                    Select a date and time to see ${planName} in ${communityName} by ${brandName}.
                </h2>
              </div>

              ${this.dateInput.render()}

              <div class="nhs-c-datepicker__content" data-slots-container>
                  <p data-month-selected class="nhs-o-typography--sans-body-1-small-medium nhs-u-text--align-center nhs-u-margin--bottom-100">${this.renderMonth()}</p>
                  <p class="nhs-o-typography--sans-label-1-xxx-small-medium-uppercase nhs-u-color--black-800 nhs-u-text--align-center nhs-u-margin--bottom-600"><span data-tour-timezone>&nbsp;</span></p>
                  <div data-date-picker>
                       ${this.slotsCarousel.render()}
                  </div>
              </div>
              <div data-selected-slot-summary></div>
              <hr class="nhs-u-height--25 nhs-u-self-stretch nhs-u-background-color--black-600" />
              <div>
                    <p class="nhs-c-label--black-900-body-1-medium-regular">Can’t find the right date or time? Call for assistance, an agent is standing by to assist you.</p>
                   ${this.salesPhone.render()}
              </div>
          </div>
      </div>`;
  }
}
